import { CdnAsset } from "#components/ui/CdnAsset.tsx";
import { channels } from "#features/conversations/types/channels.tsx";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { ConnectorChannelType } from "#types/types.ts";
import { type ImageProps, useColorSchemeValue } from "@hopper-ui/components";

interface Props extends Partial<ImageProps> {
    type: ConnectorChannelType;
    isAlt?: boolean;

    /**
     * Set on surfaces that render the mark bare, with no plate behind it, so a channel declaring
     * `iconUrlDark` switches to that light-on-dark variant on the dark scheme. Off by default:
     * most call sites sit the mark on a light plate (`core_samoyed`, `core_toad-50`) that stays
     * light in both schemes, where the light-on-dark mark would be the invisible one.
     */
    isPlateless?: boolean;
}

export const ConnectorChannelIcon = ({ type, isAlt, isPlateless, ...rest }: Props) => {
    const { tShared } = useSharedTranslation();
    // Falls back to the neutral channel for a ConnectorChannelType the wire contract still permits
    // but `channels` no longer maps (e.g. the retired native Confluence/Teams values) — same fallback
    // `getSourceTypeChannel` uses in channels.tsx.
    const { nameKey, iconUrl, iconUrlAlt, iconUrlDark } = channels[type] ?? channels.None;
    const schemeIconUrl = useColorSchemeValue(iconUrl, isPlateless ? iconUrlDark ?? iconUrl : iconUrl);

    return (
        <CdnAsset
            asset={isAlt && iconUrlAlt ? iconUrlAlt : schemeIconUrl}
            alt={tShared(nameKey)}
            {...rest}
        />
    );
};
